import styled from 'styled-components'

import {
  Button,
  DataTable,
  FormGroup,
  Input,
  Label,
  Select,
} from '@/components'
import { TInput } from '@/components/forms/form-elements/types'
import { useAxios } from '@/context/axiosContext'
import { tableStyles } from '@/styles/dataTable/style'
import commonUtils from '@/utils/commonUtils'
import React, { useEffect, useState } from 'react'
import { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'
import { Row } from 'styled-bootstrap-grid'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useDialog } from '@/context/dialogContext'
import { NoDataComponent } from '@/pages/bizMgmt/bizMgmt'
import { useRedis } from '@/hooks/useRedis'

export interface DataRow {
  histSn: number
  cmpnyCd: string
  cstId: string
  userId: string
  lastLogin: string
  accessIp: string
  loginYn: string
  loginYnNm: string
  loginFailCode: string
  loginHistMessage: string
  regId: string
  regDate: string
  updId: string
  updDate: string
  adminYn: string
  rnum: string
}

const initialSrchData = {
  srchOpt: '01',
  srchStr: '',
  loginYn: '',
}

const LoginHistMgmt: React.FC = () => {
  const axios = useAxios()

  const navigate = useNavigate()
  const location = useLocation()

  const { showConfirm } = useDialog()

  const { getComCodes, getComCodeValue } = useRedis();

  const memberSearchState = location.state?.memberSearchState

  const [data, setData] = useState<DataRow[]>([])

  const [srchData, setSrchData] = useState(initialSrchData)

  const [cstId, setCstId] = useState(location.state?.cstId || null)

  useEffect(() => {
    const prevLoginHistState = location.state?.loginHistSearchState
    if (prevLoginHistState) {
      setSrchData(prevLoginHistState)
      setData(prevLoginHistState.results)
    } else if (location.state?.cstId) {
      setCstId(location.state.cstId)
      getLoginHistList(location.state.cstId)
    } else {
      getLoginHistList()
    }
  }, [location])

  const getLoginHistList = async (cstId?: string) => {
    try {
      const response = await axios.get('/loginHistMgmt/list', {
        params: {
          adminYn: 'N',
          srchOpt: srchData.srchOpt,
          srchStr: srchData.srchStr,
          loginYn: srchData.loginYn,
          ...(cstId && { cstId: cstId }),
        },
      })
      const listData = response.data.data?.histList

      if (Array.isArray(listData)) {
        for(const data of listData){
          data.loginYnNm = getComCodeValue('LOGIN_YN', data.loginYn)?.codeNm || '';
        }
        setData(listData)
      }
    } catch (error) {
      console.error('Error fetching loginHistlist:', error)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      getLoginHistList()
    }
  }

  const handleSearch = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement
    setSrchData((prev) => ({ ...prev, [name]: value }))
    setCstId(null)
  }

  const columns: TableColumn<DataRow>[] = [
    {
      name: '순번',
      selector: (row) => row.histSn,
      sortable: true,
    },
    {
      name: 'ID',
      selector: (row) => row.userId,
      sortable: true,
    },
    {
      name: '접속IP',
      selector: (row) => row.accessIp,
      sortable: true,
      minWidth: '80px',
      maxWidth: '120px',
    },
    {
      name: '로그인성공여부',
      selector: (row) => row.loginYnNm || row.loginYn,
      sortable: true,
      center: true,
    },
    {
      name: '로그인결과코드',
      selector: (row) => row.loginFailCode,
      sortable: true,
      center: true,
    },
    {
      name: '로그인이력메시지',
      selector: (row) => row.loginHistMessage,
      sortable: true,
      grow: 3,
      maxWidth: '500px',
      minWidth: '150px',
      cell: (row) => (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.loginHistMessage}
        </div>
      ),
    },
    {
      name: '수정자',
      selector: (row) => row.updId,
      sortable: true,
      width: '100px',
    },
    {
      name: '수정일시',
      selector: (row) => row.updDate,
      format: (row) => commonUtils.formatDate(row.updDate, true),
      sortable: true,
      center: true,
      width: '180px',
    },
    {
      name: '',
      center: true,
      cell: (row) => (
        <Button
          color="neverbe"
          variant="outlined"
          onClick={() =>
            navigate('/loginHistMgmt/detail', {
              state: {
                histSn: row.histSn,
                loginHistSearchState: { ...srchData, results: data },
                memberSearchState: location.state?.memberSearchState,
              },
            })
          }
        >
          상세
        </Button>
      ),
    },
  ]

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <p style={{ marginLeft: '20px', fontSize: '20px' }}>로그인이력관리</p>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <FormGroup>
              <Label htmlFor={'loginYn'}>로그인성공여부</Label>
              <Select
                id="loginYn"
                name="loginYn"
                width="100px"
                value={srchData.loginYn}
                onChange={handleSearch}
              >
                <option value="">전체</option>
                {getComCodes('LOGIN_YN').map((item) => (
                  <option key={'loginYn'+item.code} value={item.code}>
                      {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'srchOpt'}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Select
                  id="srchOpt"
                  name="srchOpt"
                  width="140px"
                  value={srchData.srchOpt}
                  onChange={handleSearch}
                >
                  <option value="01">ID</option>
                  <option value="02">접속IP</option>
                  <option value="03">로그인결과코드</option>
                </Select>
                <Input
                  placeholder="검색어"
                  width="300px"
                  id="srchStr"
                  name="srchStr"
                  value={srchData.srchStr}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                />
                <Button onClick={() => getLoginHistList()} color="neverbe">
                  검색
                </Button>
                {memberSearchState && (
                  <Button
                    variant="outlined"
                    color="neverbe"
                    onClick={() =>
                      showConfirm({
                        title: '회원관리 화면으로 이동할까요?',
                        onConfirm() {
                          navigate('/memberMgmt', {
                            state: { prevSearchState: memberSearchState },
                          })
                        },
                      })
                    }
                  >
                    회원관리
                  </Button>
                )}
              </FlexWrapper>
            </FormGroup>
          </div>
        </div>
      </div>

      <TableWrapper>
        <DataTable
          columns={columns}
          data={data}
          customStyles={tableStyles}
          pagination={true}
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
          defaultSortAsc={false}
          noDataComponent={<NoDataComponent />}
        />
      </TableWrapper>
    </>
  )
}

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 700px;
`
export default LoginHistMgmt
